// WebGL2 Rive player with robust looping play. Unlike SimpleRive (canvas), this
// uses the WebGL2 renderer. Plays whatever the file exposes — a preferred
// animation/state-machine by name if given, else state machines, else animations.
//
// WebGL2 contexts are capped (~16/page). For grids/lists, gate mounting on
// viewport visibility (pass `lazy`, or gate from the caller) so only on-screen
// instances hold a context.
import { useEffect, useRef, useState } from 'react';
import { useRive, Layout, Fit, Alignment, EventType } from '@rive-app/react-webgl2';

const norm = (s: string) => s.toLowerCase().replace(/[\s_-]/g, '');
function matchName(names: string[] | undefined, preferred?: string) {
  if (!names?.length || !preferred) return undefined;
  return names.find((n) => norm(n) === norm(preferred)) || names.find((n) => norm(n).includes(norm(preferred)));
}

function RivePlayer({ src, preferredName, loop = false, className = 'w-full h-full', onReady }: { src: string; preferredName?: string; loop?: boolean; className?: string; onReady?: () => void }) {
  const { rive, RiveComponent } = useRive({
    src,
    autoplay: true,
    layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
  });

  // fired once the animation file is loaded and its instance exists — lets
  // callers drop static fallbacks (e.g. the headline Vovy) at that moment
  const readyCb = useRef(onReady);
  readyCb.current = onReady;
  useEffect(() => { if (rive) readyCb.current?.(); }, [rive]);

  useEffect(() => {
    if (!rive) return;
    const playBest = () => {
      try {
        const sms = rive.stateMachineNames;
        const anims = rive.animationNames;
        const pref = matchName(sms, preferredName) || matchName(anims, preferredName);
        if (pref) { rive.play(pref); return; }
        if (sms?.length) rive.play(sms);
        else if (anims?.length) rive.play(anims);
        else rive.play();
      } catch { /* noop */ }
    };
    playBest();

    // Seamless loop: replay as soon as the animation reports it stopped.
    let loopHandler: (() => void) | undefined;
    let intervalId: number | undefined;
    if (loop) {
      loopHandler = () => { try { playBest(); } catch { /* noop */ } };
      rive.on(EventType.Stop, loopHandler);
    } else {
      intervalId = window.setInterval(() => { try { if (!rive.isPlaying) playBest(); } catch { /* noop */ } }, 1200);
    }

    return () => {
      if (loopHandler) { try { rive.off(EventType.Stop, loopHandler); } catch { /* noop */ } }
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [rive, preferredName, loop]);

  return <RiveComponent className={className} />;
}

function LazyRive({ src, preferredName, loop = false, className = 'w-full h-full', rootMargin = '200px' }: { src: string; preferredName?: string; loop?: boolean; className?: string; rootMargin?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { rootMargin });
    io.observe(node);
    return () => io.disconnect();
  }, [rootMargin]);
  return <div ref={ref} className={className}>{visible && <RivePlayer src={src} preferredName={preferredName} loop={loop} className="w-full h-full" />}</div>;
}

export default function WebglRive({
  src, preferredName, loop = false, className, lazy = false, rootMargin, onReady,
}: {
  src: string;
  preferredName?: string;
  /** Replay the animation as soon as it ends. */
  loop?: boolean;
  className?: string;
  /** Mount the rive only while near the viewport (for grids/lists). */
  lazy?: boolean;
  rootMargin?: string;
  /** Called once the .riv file is loaded and playing. */
  onReady?: () => void;
}) {
  return lazy
    ? <LazyRive src={src} preferredName={preferredName} loop={loop} className={className} rootMargin={rootMargin} />
    : <RivePlayer src={src} preferredName={preferredName} loop={loop} className={className} onReady={onReady} />;
}
